import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { ISearchResult } from 'src/app/shared/interfaces/search.interface';
import { SearchService } from 'src/app/shared/services/search.service';
import { defer, finalize, iif, map, Observable, of, switchMap, tap } from 'rxjs';
import { ResetSearch, SearchByTerm } from './search.actions';
import { SearchOperations } from './search.operations';

export interface SearchStateModel {
  results: ISearchResult[];
  searchTerm: string;
  lastEvaluatedKey: string;
  prelastEvaluatedKey: string;
  isSearching: boolean;
}

@State<SearchStateModel>({
  name: 'searchState',
  defaults: {
    results: [],
    searchTerm: null,
    lastEvaluatedKey: null,
    prelastEvaluatedKey: null,
    isSearching: false
  }
})
@Injectable()
export class SearchState {
  private readonly searchService = inject(SearchService);

  private searchItemLimit: number = 10;

  @Selector()
  static results(state: SearchStateModel): ISearchResult[] {
    return state.results;
  }

  @Selector()
  static isSearching(state: SearchStateModel): boolean {
    return state.isSearching;
  }

  @Selector()
  static searchTerm(state: SearchStateModel): string {
    return state.searchTerm;
  }

  @Selector()
  static hasMoreResults(state: SearchStateModel): boolean {
    return !!state.lastEvaluatedKey && state.lastEvaluatedKey !== state.prelastEvaluatedKey;
  }

  @Action(SearchByTerm)
  searchByTerm(ctx: StateContext<SearchStateModel>, { searchTerm }: SearchByTerm): Observable<void> {
    const state = ctx.getState();

    if (searchTerm === null || searchTerm === '') {
      ctx.dispatch(ResetSearch);
      return of(void 0);
    }

    if (searchTerm === undefined) {
      searchTerm = state.searchTerm;

      if (!searchTerm) {
        return of(void 0);
      }
    }

    const isNew = searchTerm !== state.searchTerm;

    return iif(
      () => isNew,
      defer(() => ctx.dispatch(ResetSearch)),
      of(null)
    ).pipe(
      switchMap(() => {
        const { lastEvaluatedKey: currentLastEvaluatedKey, prelastEvaluatedKey } = ctx.getState();

        /**
         * Due to DynamoDB pagination, reaching end of any number of page results in `lastEvaluatedKey` to be empty
         * Meaning that if the previous `lastEvaluatedKey`, namely, `prelastEvaluatedKey` exists but the current
         * `lastEvaluatedKey` does not - it means the end of the pagination has been reached
         * NOTE: It is also important to check if the currentLastEvaluatedKey is the same as prelastEvaluatedKey IF currentLastEvaluatedKey exists
         * Else scrolling down the search results will keep firing the search action even if we are at the end of the results
         */
        if ((!currentLastEvaluatedKey && prelastEvaluatedKey) || (!!currentLastEvaluatedKey && currentLastEvaluatedKey === prelastEvaluatedKey)) {
          return of(void 0);
        }

        ctx.patchState({ isSearching: true, prelastEvaluatedKey: currentLastEvaluatedKey, searchTerm });

        return this.searchService.search(searchTerm, this.searchItemLimit, isNew ? null : ctx.getState().lastEvaluatedKey).pipe(
          tap(results => {
            const { content, lastEvaluatedKey } = results;

            /**
             * Due to DynamoDB pagination, reaching end of any number of page results in `content` to be empty
             * Do not patch append state unless we have contents
             */
            if (content?.length) {
              const currentResults = ctx.getState().results;
              const isLastPage = !lastEvaluatedKey || content.length < this.searchItemLimit;

              const patchObject = {
                results: SearchOperations.sortResultsByRelevance(currentResults.concat(content)),
                lastEvaluatedKey: lastEvaluatedKey ?? 'unset',
                ...(isLastPage && { prelastEvaluatedKey: lastEvaluatedKey ?? 'unset' })
              };

              ctx.patchState(patchObject);
            }
          }),
          map((): void => void 0),
          finalize(() => {
            ctx.patchState({ isSearching: false });
          })
        );
      })
    );
  }

  @Action(ResetSearch)
  resetSearch(ctx: StateContext<SearchStateModel>): void {
    ctx.setState({
      results: [],
      searchTerm: null,
      lastEvaluatedKey: null,
      prelastEvaluatedKey: null,
      isSearching: false
    });
  }
}
